import { useMemo, useState } from "react";
import { calls as Api, type Outcome, type TradeCall } from "@tvx/api-client";
import { useAsync } from "../lib/useAsync";
import { Shim, SkeletonRows } from "../components/Shimmer";
import { useSelection, TickAll, Tick, BulkActions } from "../components/Bulk";
import { TradeCallForm } from "./TradeCallForm";
import { Modal } from "../components/Modal";

const num = (n: number | null) => (n == null ? "—" : Number(n).toLocaleString("en-IN"));

const OUTCOME_LABEL: Record<Outcome, string> = {
  open: "Open",
  target_hit: "Target hit",
  sl_hit: "SL hit",
  closed: "Closed",
};
const OUTCOME_COLOUR: Record<Outcome, string> = {
  open: "rgba(var(--wash), calc(.06 * var(--washx)))",
  target_hit: "rgba(62,207,142,.14)",
  sl_hit: "rgba(240,100,91,.13)",
  closed: "rgba(var(--wash), calc(.06 * var(--washx)))",
};

const FILTERS = ["all", "open", "published", "draft", "Equity", "F&O", "Forex"] as const;

export function TradeCalls() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("all");
  const [editing, setEditing] = useState<TradeCall | "new" | null>(null);
  const [closing, setClosing] = useState<{ call: TradeCall; outcome: Outcome } | null>(null);

  const rows = useMemo(() => {
    const all = data?.calls ?? [];
    switch (filter) {
      case "open": return all.filter((c) => c.outcome === "open" && c.state === "published");
      case "published": return all.filter((c) => c.state === "published");
      case "draft": return all.filter((c) => c.state === "draft");
      case "Equity": return all.filter((c) => c.segment === "Equity");
      case "F&O": return all.filter((c) => c.segment === "F&O");
      case "Forex": return all.filter((c) => c.segment === "Forex");
      default: return all;
    }
  }, [data, filter]);

  const openCount = (data?.calls ?? []).filter((c) => c.outcome === "open" && c.state === "published").length;
  const sel = useSelection(rows);

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Trade calls</h1>
          <p style={{ fontSize: 13.5, color: "var(--tx2)", marginTop: 7 }}>
            {loading && !data ? <Shim w={210} h={12} style={{ marginTop: 3 }} /> : (
              <>
                {openCount} open
                {data?.stats.closedCount
                  ? ` · ${data.stats.hitRate}% of ${data.stats.closedCount} closed hit target · 90 days`
                  : " · nothing closed yet, so there is no hit rate to show"}
              </>
            )}
          </p>
        </div>
        <button className="b bg" type="button" onClick={() => setEditing("new")}>New call</button>
      </div>

      {error ? <div className="err">Could not load calls — {error.message}</div> : null}

      <div className="crd" style={{ padding: "20px 22px" }}>
        <div className="sg" style={{ marginBottom: 16 }}>
          {FILTERS.map((f) => (
            <span key={f} {...(filter === f ? { "data-on": "" } : {})} onClick={() => setFilter(f)}>
              {f === "all" ? "All" : f === "Forex" ? "Forex" : f[0]!.toUpperCase() + f.slice(1)}
            </span>
          ))}
        </div>

        <BulkActions
          path="/calls" noun="call"
          rows={sel.picked.map((c) => ({ id: c.id, label: String(c.instrument) }))}
          actions={[["publish", "Publish"], ["unpublish", "Unpublish"]]}
          onDone={async () => { sel.clear(); await reload(); }}
        />

        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={sel.allOn} some={sel.picked.length > 0} onChange={sel.toggleAll} />
                <th>Instrument</th><th>Side</th><th>Entry</th><th>Target</th><th>SL</th>
                <th>Result</th><th>State</th><th />
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <SkeletonRows cols={9} tick />
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="muted" style={{ padding: "26px 8px" }}>
                  {data?.calls.length ? "Nothing matches this filter."
                    : "No calls yet. The first one you publish appears on the site immediately."}
                </td></tr>
              ) : rows.map((c) => (
                <tr key={c.id} style={sel.isOn(c.id) ? { background: "rgba(var(--goldrgb), .05)" } : undefined}>
                  <Tick on={sel.isOn(c.id)} label={String(c.instrument)}
                    onChange={(v) => sel.toggle(c.id, v)} />
                  <td>
                    <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                      {c.instrument}
                      {c.plan_tier && c.plan_tier !== "all" ? (
                        <span className="tg" style={{ fontSize: 10, padding: "2px 6px", background: "rgba(var(--goldrgb), .15)", color: "var(--goldtx)" }}>
                          {c.plan_tier.toUpperCase()}
                        </span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--tx3)" }}>
                      {c.segment}{c.timeframe ? ` · ${c.timeframe}` : ""}
                    </div>
                  </td>
                  <td style={{ color: c.side === "Buy" ? "var(--up)" : "var(--down)" }}>{c.side}</td>
                  <td>{num(c.entry)}</td>
                  <td>{num(c.target)}</td>
                  <td>{num(c.stop_loss)}</td>
                  <td>
                    <span className="tg" style={{ background: OUTCOME_COLOUR[c.outcome] }}>
                      {OUTCOME_LABEL[c.outcome]}
                    </span>
                    {c.outcome_pct != null ? (
                      <span style={{ marginLeft: 6, fontSize: 11.5,
                        color: c.outcome_pct < 0 ? "var(--down)" : "var(--up)" }}>
                        {c.outcome_pct < 0 ? "−" : "+"}{Math.abs(c.outcome_pct).toFixed(1)}%
                      </span>
                    ) : null}
                  </td>
                  <td className="muted">{c.state}</td>
                  <td className="right" style={{ whiteSpace: "nowrap" }}>
                    {c.outcome === "open" && c.state === "published" ? (
                      <>
                        <button className="b bo" style={{ padding: "6px 10px", fontSize: 11.5 }}
                          type="button" onClick={() => setClosing({ call: c, outcome: "target_hit" })}>Target</button>
                        <button className="b bo" style={{ padding: "6px 10px", fontSize: 11.5, marginLeft: 6 }}
                          type="button" onClick={() => setClosing({ call: c, outcome: "sl_hit" })}>SL</button>
                      </>
                    ) : null}
                    <button className="b bo" style={{ padding: "6px 12px", fontSize: 11.5, marginLeft: 6 }}
                      type="button" onClick={() => setEditing(c)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <TradeCallForm
          call={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      ) : null}

      {closing ? (
        <CloseCallModal
          call={closing.call}
          initialOutcome={closing.outcome}
          onCancel={() => setClosing(null)}
          onSaved={async () => { setClosing(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}

function CloseCallModal({ call, initialOutcome, onCancel, onSaved }: {
  call: TradeCall;
  initialOutcome: Outcome;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [outcome, setOutcome] = useState<Outcome>(initialOutcome);
  const [pct, setPct] = useState<string>(() => {
    if (call.entry && call.target && initialOutcome === "target_hit") {
      const p = ((call.target - call.entry) / call.entry) * 100 * (call.side === "Sell" ? -1 : 1);
      return p.toFixed(1);
    }
    if (call.entry && call.stop_loss && initialOutcome === "sl_hit") {
      const p = ((call.stop_loss - call.entry) / call.entry) * 100 * (call.side === "Sell" ? -1 : 1);
      return p.toFixed(1);
    }
    return "";
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    setError("");
    const val = pct.trim() === "" ? null : Number(pct);
    if (val !== null && !Number.isFinite(val)) {
      setError("Please enter a valid percentage number.");
      return;
    }
    setSubmitting(true);
    try {
      await Api.update(call.id, {
        ...call,
        outcome,
        outcome_pct: val,
      });
      await onSaved();
    } catch (e) {
      setSubmitting(false);
      setError((e as Error).message);
    }
  }

  return (
    <Modal
      title={`Mark Result · ${call.instrument} (${call.side})`}
      onClose={onCancel}
      footer={
        <>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button className="b bg" type="button" onClick={() => void submit()} disabled={submitting}>
            {submitting ? "Saving…" : "Save Result"}
          </button>
        </>
      }
    >
      {error ? <div className="err" style={{ marginBottom: 16 }}>{error}</div> : null}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 18, background: "var(--wash, rgba(255,255,255,.03))", padding: "12px 14px", borderRadius: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: "var(--tx3)" }}>Entry</div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{num(call.entry)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: "var(--tx3)" }}>Target</div>
          <div style={{ fontWeight: 600, fontSize: 14, color: "var(--up)" }}>{num(call.target)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: "var(--tx3)" }}>Stop loss</div>
          <div style={{ fontWeight: 600, fontSize: 14, color: "var(--down)" }}>{num(call.stop_loss)}</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 14 }}>
        <div className="fld">
          <label className="lbl">Outcome</label>
          <select className="inp" value={outcome} onChange={(e) => setOutcome(e.target.value as Outcome)}>
            <option value="target_hit">Target hit</option>
            <option value="sl_hit">Stop loss hit</option>
            <option value="closed">Closed manually</option>
          </select>
        </div>

        <div className="fld">
          <label className="lbl">Percentage moved (%)</label>
          <input
            className="inp"
            type="number"
            step="any"
            placeholder="e.g. 4.5 or -1.8"
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            autoFocus
          />
        </div>
      </div>
    </Modal>
  );
}
